"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.flatFiltersToTree = flatFiltersToTree;
exports.buildTenantScopedWhere = buildTenantScopedWhere;
exports.buildTenantScopedOrderBy = buildTenantScopedOrderBy;
const operator_handlers_1 = require("./operator-handlers");
const types_1 = require("./types");
function flatFiltersToTree(filters, logicOperator = 'and') {
    const conditions = filters.map((f) => ({
        type: 'condition',
        field: f.field,
        operator: f.operator,
        value: f.value,
    }));
    return { type: logicOperator === 'or' ? 'or' : 'and', filters: conditions };
}
function validateCondition(schema, condition, ignoredSet, rejected) {
    if (ignoredSet.has(condition.field)) {
        return null;
    }
    if (!types_1.VALID_FIELD_NAME_PATTERN.test(condition.field)) {
        rejected.push(condition.field);
        return null;
    }
    if (!Object.hasOwn(schema.fields, condition.field)) {
        rejected.push(condition.field);
        return null;
    }
    const fieldDef = schema.fields[condition.field];
    if (fieldDef.allowedOperators && !fieldDef.allowedOperators.includes(condition.operator)) {
        rejected.push(`${condition.field}:${condition.operator}`);
        return null;
    }
    return fieldDef;
}
function buildFieldClause(fieldDef, condition) {
    try {
        return buildFieldClauseUnsafe(fieldDef, condition);
    }
    catch {
        return null;
    }
}
function buildFieldClauseUnsafe(fieldDef, condition) {
    const { options } = fieldDef;
    switch (options.type) {
        case types_1.FieldType.String: {
            const column = options.column ?? condition.field;
            return (0, operator_handlers_1.buildStringOperatorClause)(column, condition.operator, condition.value, {
                nonNullable: options.nonNullable,
            });
        }
        case types_1.FieldType.Boolean: {
            const column = options.column ?? condition.field;
            return (0, operator_handlers_1.buildBooleanOperatorClause)(column, condition.operator, condition.value);
        }
        case types_1.FieldType.Number: {
            const column = options.column ?? condition.field;
            return (0, operator_handlers_1.buildNumberOperatorClause)(column, condition.operator, condition.value);
        }
        case types_1.FieldType.Date: {
            const column = options.column ?? condition.field;
            return (0, operator_handlers_1.buildDateOperatorClause)(column, condition.operator, condition.value);
        }
        case types_1.FieldType.Relation: {
            return (0, operator_handlers_1.buildRelationOperatorClause)(options.relation, options.searchColumns, condition.operator, condition.value);
        }
        case types_1.FieldType.Custom: {
            return options.buildWhere(condition.operator, condition.value);
        }
    }
}
function processFilterNode(node, schema, ignoredSet, rejected) {
    if (node.type === 'condition') {
        const fieldDef = validateCondition(schema, node, ignoredSet, rejected);
        if (!fieldDef) {
            return null;
        }
        return buildFieldClause(fieldDef, node);
    }
    const childClauses = node.filters
        .map((child) => processFilterNode(child, schema, ignoredSet, rejected))
        .filter((clause) => clause !== null && Object.keys(clause).length > 0);
    if (childClauses.length === 0) {
        return null;
    }
    if (childClauses.length === 1) {
        return childClauses[0];
    }
    if (node.type === 'or') {
        return { OR: childClauses };
    }
    return { AND: childClauses };
}
function buildTenantScopedWhere(tenancyClause, schema, userFilters, additionalFilters) {
    const rejected = [];
    const ignoredSet = new Set(schema.ignoredFields ?? []);
    const userClause = userFilters ? processFilterNode(userFilters, schema, ignoredSet, rejected) : null;
    const andConditions = [];
    if (Object.keys(tenancyClause).length > 0) {
        andConditions.push(tenancyClause);
    }
    if (additionalFilters) {
        for (const filter of additionalFilters) {
            if (Object.keys(filter).length > 0) {
                andConditions.push(filter);
            }
        }
    }
    if (userClause && Object.keys(userClause).length > 0) {
        andConditions.push(userClause);
    }
    if (andConditions.length === 0) {
        return { where: {}, rejected };
    }
    return { where: { AND: andConditions }, rejected };
}
function buildFieldOrderBy(fieldDef, sortOption) {
    const { options } = fieldDef;
    const direction = sortOption.direction;
    switch (options.type) {
        case types_1.FieldType.Relation: {
            return [];
        }
        case types_1.FieldType.Custom: {
            if (options.buildOrderBy) {
                return options.buildOrderBy(direction);
            }
            return [];
        }
        case types_1.FieldType.String:
        case types_1.FieldType.Boolean:
        case types_1.FieldType.Number:
        case types_1.FieldType.Date: {
            const column = options.column ?? sortOption.field;
            return [{ [column]: direction }];
        }
    }
}
function buildTenantScopedOrderBy(schema, sort, defaultOrderBy) {
    const fallback = defaultOrderBy ?? [{ createdAt: 'desc' }];
    if (!sort || sort.length === 0) {
        return fallback;
    }
    const orderByItems = [];
    for (const sortOption of sort) {
        if (!types_1.VALID_FIELD_NAME_PATTERN.test(sortOption.field)) {
            continue;
        }
        if (!Object.hasOwn(schema.fields, sortOption.field)) {
            continue;
        }
        const fieldDef = schema.fields[sortOption.field];
        const items = buildFieldOrderBy(fieldDef, sortOption);
        orderByItems.push(...items);
    }
    return orderByItems.length > 0 ? orderByItems : fallback;
}
//# sourceMappingURL=tenant-scoped-query-builder.js.map